import { StringComparisonTypes } from "Configuration";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue, IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { invariant } from "Global/Base/Utils/AssertUtils";
import { IRecordIdentification } from "OW.Remoting/Remoting/DataTypes/RecordIdentification";
import { ErrorHandler } from "UI/Errors";
import { AdditionalOrdering, IOrderBy, OrderType } from "../../../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent, EventAwaiter } from "../../../../Global/Base/Events";
import { DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { Code } from "../../../../Global/Base/Utils/EventUtils";
import { FunctionUtils } from "../../../../Global/Base/Utils/FunctionUtils";
import { NumberUtils } from "../../../../Global/Base/Utils/NumberUtils";
import { ConditionOperator, StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { Globalization } from "../../../../Global/Dom/Globalization";
import { BasicDataType } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { NullOWObject, OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { SerializeFuncs } from "../../../../OW.Remoting/Remoting/Serializers";
import { IBussyAwaitable, IDataLoadingAwaitable } from "../../../UI.BaseControls/ControlInterfaces";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { IMultiEditObject } from "../../../UI.Controls/DataSource/MultiEdit/MEOWObject";
import { IDataGridSelectionChangedEvent } from "../../../UI.Controls/IEvent";
import { FormResultType } from "../XGControls/Forms/FormResult";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { IForm } from "../XGControls/IForm";
import { ModalWindowType } from "../XGControls/ModalWindow";
import { IEditableControl, IRequiresActualObject, IRequiresClassName, IVersioned } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";
import { IFRMParams } from "./FormParameters";
import { LOVEditMaster, LOVEditMasterParams } from "./LOVEditMaster";

const logger = LazyLoadingSectionLogger.create(LoggingSection.lOVEdit);

/**
 * Podmnožina údajů z {@link IPublicAttributeInfo} nutná k práci s atributy v LOVEditu.
 * Obsahuje údaje o typu atributu.
 */
type ColumnType = Pick<IPublicAttributeInfo, "type" | "additionalTypeName">;

export class LOVEdit extends LOVEditMaster implements IEditableControl, IRequiresActualObject, IRequiresClassName, IBussyAwaitable, IDataLoadingAwaitable, IVersioned {
    //#region Fields
    private _recordId: string | null = null;
    private readonly _showAttrs: string[];

    protected wasClicked: boolean = false; // True pokud došlo k prvnímu kliknutí na tlačítko pro otevření detailu LOV (využívá se při kontrolování double clicku)

    // Datový typ atributu v showattrs - potřebné pro getování v našeptávači
    protected _columnTypes: { [key: string]: ColumnType } = {};
    private _waitForActualObjectSetDeffer: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();
    private _orderBy: IOrderBy[];
    private _dateRelAttr: string;
    private _lastObject: OWObject;
    private _lovStyle: LOVType;
    private _readVersioned = false;
    private _showOnlyDetail = false;
    private _showAsLink = false;
    private _validationClassName: string;
    //#endregion Listeners

    //#region Properties
    //Aktuální objekt na detailu - master objektu na LOV
    public set actualObject(value: OWObject) {
        this._setActualObject(value);
    }

    //Aktuální objekt na detailu - master objektu na LOV
    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public waitForActualObjectSet(): Promise<boolean> {
        return this._waitForActualObjectSetDeffer.promise;
    }

    /**
     * Vyžadováno pro otevírání modálního formuláře
     */
    public override set ownerForm(value: IForm) {
        this._ownerForm = value;
    }

    public get editWindow(): string | null {
        return this._editWindow;
    }

    //#region controlValue

    /** Promenna ktera obsahuje informaci o tom, zda je v editacnim rezimu nebo ne */
    public override set isEditing(value: boolean) {
        if (this._showAsLink) {
            this._isEditing = false;
            return;
        }
        super.isEditing = value;
    }

    public override get isEditing(): boolean {
        return this._showAsLink ? false : this._isEditing;
    }

    private _competenceCondition: string | null = null;

    private async _getCompetenceCondition(cond: string): Promise<string> {
        if (this._competenceCondition === null && !(this.actualObject instanceof IMultiEditObject)) {
            invariant(this.actualObject, "[LOVEdit] Cannot get competence condition because actualObject is NULL");
            invariant(this.actualObject.instanceID, "[LOVEdit] Cannot get competence condition because value of `instanceId` is null");
            this._competenceCondition = await OWManager.getAttributeLOVConditions(this.actualObject.instanceID, this.actualObject.objectName, this.attrName, { ...this._getRequestContext(), roles: this._actualObject.editRoleIds });
        }

        cond = StringUtils.combineConditions(ConditionOperator.and, cond, this._competenceCondition);
        return cond;
    }

    public override get controlValue(): any { // Zde by měl být správně návratový typ "OWObject | string | number | null", ale nyní to nelze, protože předek (Edit) má u této property string, takže OWObject a number neprojde kompilací
        // pokud je naplněno validationClassName, tak ID nestačí a je potřeba vracet instanci
        if (this._validationClassName && this._recordId)
            return this._lastObject;
        return this._recordId;
    }

    /**
     * Nastaví hodnotu editu
     */
    public override set controlValue(value: any) {
        if (this._whispererDataLoading) {
            return;
        }

        this.showLoader();

        if (this._waitOnDataLoadedDeffer.isSettled) {
            this._waitOnDataLoadedDeffer = PromiseUtils.deferStateful<boolean>();
        }

        if (value instanceof IOWObject && !(value instanceof NullOWObject) && value.objectName != null && value.instanceID != null) {
            this._recordId = value.id;
            this._lastObject = (value as OWObject);
            const array = this._showAttrs.slice(0);

            this._updateValue(array, (value as OWObject), "");
        } else {
            this._updateFilledClass(false);
            this.innerElement.value = "";

            if (this._recordId) {
                this._recordId = undefined;
            }

            this.setValid();
            this._waitOnDataLoadedDeffer.resolve();
            this.hideLoader();
        }

        this.element.classList.toggle("ShowAsLinkDisabled", this._showAsLink && (!value || (value instanceof NullOWObject)));
    }
    //#endregion controlValue

    /**
     * Příznak, zda se mají číst verzované záznamy.
     */
    public get readVersioned(): boolean {
        return this._readVersioned;
    }

    public set readVersioned(value: boolean) {
        this._readVersioned = value;
    }

    //#region Enabled
    public override set enabled(value: boolean) {
        super.enabled = value;
    }

    public override get enabled(): boolean {
        return this._enabled;
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: LOVEditMasterParams) {
        super(params);
        const { element } = params;

        const attrs: string = element.getAttribute("data-showattrs");

        if (attrs)
            this._showAttrs = attrs.split(";");
        else
            logger.warn(`LOVEdit[${this.attrName}] - attribute ShowAttrs not defined`);

        this._lovStyle = this.getDataAttr("lovstyle")?.toLowerCase() as LOVType ?? LOVType.modal;

        const validClassName = this.getDataAttr("validationClassName");
        if (validClassName?.length > 0)
            this._validationClassName = validClassName;

        this._showAsLink = this.getBoolDataAttr("ShowAsLink");
        this._showOnlyDetail = this.getBoolDataAttr("ShowOnlyDetail");

        if (this._showAsLink && this._editWindow) {
            this.element.classList.add("ShowAsLink", "ShowAsLinkDisabled");
            this.element.removeChild(this._lovButton);
        }

        this._setListeners();
        this._waitForClassNameSetDeffer.resolve("");
    }
    //#endregion Constructors

    //#region Methods
    protected override async _createWhispererColumns(): Promise<void> {
        this._createColumns(this._showAttrs);
        if (this._showAttrs != null && Object.keys(this._columnTypes).length === 0) {
            await this._fillColumnTypes();
        }
    }

    /**
     * Událost o tom, že byl nastaven (neprázdný) actualObject
     */
    public readonly actualObjectSet: GlobalEvent<LOVEdit, OWObject> = new GlobalEvent<LOVEdit, OWObject>();

    private _waitForClassNameSetDeffer: DeferredStateful<string> = PromiseUtils.deferStateful<string>();

    private async _getClassName(): Promise<string> {
        let className: string;

        if (this._validationClassName)
            className = this._validationClassName;
        else {
            const pos = this.attrName.indexOf("|");
            if (pos > -1) {
                className = this.attrName.substring(pos + 1, this.attrName.length);
                this.attrName = this.attrName.substring(0, pos);
            } else {
                className = await this.actualObject?.getObjectAttributeClass(this.attrName);
                if (!className) {
                    const metaData = await this.actualObject?.classInfo.getAttributeInfo(this.attrName);
                    if (metaData && "className" in metaData) {
                        className = metaData.className;
                    } else {
                        className = null;
                    }
                }
            }
        }
        this.className = className;
        this._waitForClassNameSetDeffer.resolve(className);

        return className;
    }

    public async getClassName(): Promise<string> {
        if (this._waitForClassNameSetDeffer.isSettled) {
            this._waitForClassNameSetDeffer = PromiseUtils.deferStateful<string>();
            let skipGetting = false;
            if (!this.actualObject) {
                try {
                    await EventAwaiter.createEventAwaiter(this.actualObjectSet, 300);
                } catch (e) {
                    this.className = "";
                    this._waitForClassNameSetDeffer.resolve("");
                    skipGetting = true;
                }
            }
            if (!skipGetting) {
                await this._getClassName();
            }
        }

        // These two lines have to be like this. We have to wait for the promise of the deferred and then explicitly
        // return the value from the promiseValue field, because the value itself isn't returned implicitly from
        // the promise of the deferred.
        await this._waitForClassNameSetDeffer.promise;
        return this._waitForClassNameSetDeffer.promiseValue;
    }

    private async _setActualObject(value: OWObject): Promise<void> {
        if (this._isDisposed) { // tato metoda je volaná ze setteru, který nečeká na dokončení, proto před zpracováním této metody může nastat dispose, proto je nutné ho na začátku kontrolovat
            return;
        }

        if (value != null) {
            this._actualObject = value;
            this.actualObjectSet.trigger(this, value);
            this._waitForActualObjectSetDeffer.resolve(true);

            await this.getClassName();

            if (!this.className)
                return;

            if (this._showAttrs) {
                this._fillColumnTypes();
            }

            if (this._competenceCondition) {
                this._competenceCondition = null;
            }

            this._mainCondition = await this._buildMainConditionAsync();

            this._currentConditionParams = undefined;

            const objectAttributeChangedHandler = async (obj: OWObject, attrName: string) => {
                if (StringUtils.compareCaseInsensitive(attrName, "ID")) {
                    obj.attributeChanged.remove(objectAttributeChangedHandler); // dále již nepotřebujeme
                }
                if (this._competenceCondition) {
                    this._competenceCondition = null;
                }
                if (this._dateRelAttr != null && this._dateRelAttr === attrName) {
                    this._mainCondition = await this._buildMainConditionAsync();
                }
            };
            value.attributeChanged.add(objectAttributeChangedHandler);
        } else {
            if (this._whisperer)
                this._whisperer.clearDataRows();
            this.hideDropDown();
            this._clearActualObject();
            this.hideLoader();
        }
    }

    private async _fillColumnTypes(): Promise<void> {
        try {
            const atrInfo = await ModelDescriptionsManager.instance.getAttributesInfo(this.className, this._showAttrs);
            for (const attr of atrInfo) {
                const { attributeName, type, additionalTypeName } = attr;
                this._columnTypes[attributeName] = {
                    type,
                    additionalTypeName,
                };
            }
        } catch (e) {
            logger.error(`LOVEdit[${this.attrName}] - error during getting attribute types: ${e}`);
        }
    }

    private _clearActualObject(): void {
        if (this._waitForActualObjectSetDeffer.isSettled)
            this._waitForActualObjectSetDeffer = PromiseUtils.deferStateful<boolean>();

        this._actualObject = null;
        this.hideLoader();
        this.innerElement.value = "";
        this._updateFilledClass(false);
    }

    /**
     * Umožňuje čekat na běžící procesy na komponentě
     */
    public waitForPendingActions(): Promise<void> {
        return this._pendingTask ? this._pendingTask : Promise.resolve();
    }

    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    public cancelPendingActions(): void {
        if (this._pendingTaskCTS) {
            this._pendingTaskCTS.cancel();
            this._pendingTask = null;
        }
    }

    public override validate(_element?: HTMLInputElement): boolean {
        if (this.required && this.innerElement.value === "") {
            // Pokud je pole povinné a je nevyplněné, tak má po validaci už zůstat v nevalidním stavu - validaci povinností provádí až validace detailu (třeba při ukládání), takže zde stav neměním
        } else if (this.innerElement && this.innerElement.value !== "" && !this._recordId) {
            // Pokud je pole povinné a obsahu neodpovídá žádný objekt, tak je nevalidní
            this.setInvalid("GUI-611969"); // GUI-611969 = Zadaná hodnota neexistuje.
        } else {
            this.setValid();
        }

        return this.isValid;
    }

    /**
     * Pokusí se získat informace o typu atributu na základě {@link LOVEdit._columnTypes} a zadaného jména atributu.
     * Nejdříve se pokusí získat typ pro case sensitive název atributu. Pokud se to nepovede, vyhledává case insensitive.
     * Pokud se nepovede ani to, vyhodí chybu.
     * @param attribute Jméno atributu, pro který se získává typ.
     * @private
     */
    private _getAttributeType(attribute: string): ColumnType {
        const caseMatchType = this._columnTypes[attribute];
        if (caseMatchType) {
            return caseMatchType;
        } else {
            const lowerCaseAttribute = attribute.toLowerCase();
            const caseInsensitiveMatchingKey = Object.keys(this._columnTypes).find((attrKey) => attrKey.toLowerCase() === lowerCaseAttribute);
            if (caseInsensitiveMatchingKey) {
                return this._columnTypes[caseInsensitiveMatchingKey];
            } else {
                throw new Error(`[LOVEdit] Data type for attribute ${attribute} is not defined in known attributes: ${JSON.stringify(this._columnTypes)}`);
            }
        }
    }

    // Přidá do podmínky jeden atribut
    private async _buildConditionPart(attribute: string, input: string): Promise<string> {
        let condition: string = "";
        input = input.replace(new RegExp("'", "g"), "''"); // bude možné nahradit za input.replaceAll("'", "''") jakmile bude nasazena 12. edice ECMA-262

        const { type: attributeType, additionalTypeName } = this._getAttributeType(attribute);

        if (NumberUtils.isWholeNumberType(attributeType) && /^[+-]?\d+$/.test(input)) {
            condition = `${attribute} = ${Number(input)}`; //`
        } else {
            const actualType = SerializeFuncs.getTypeDescription(attributeType, attribute, additionalTypeName);
            if (actualType.basicType === BasicDataType.String) {
                if (attributeType === "LocalizedText") {
                    const atrInfo = await ModelDescriptionsManager.instance.getAttributesInfo(this.className, [attribute]);
                    for (const attr of atrInfo)
                        attribute = attr.displayAttributeName ? `${attr.attributeName}.${attr.displayAttributeName}` : attr.attributeName; //`
                }
                const paramsValue = this._defaultWhisperer === StringComparisonTypes.startsWith ? `${input}%` : `%${input}%`;
                condition = `:Linguistic.LIKE(${attribute}, '${paramsValue}')`;
            } else if (actualType.basicType === BasicDataType.Enum) {
                const data = await OWManager.getEnum(actualType.serverType.toString());
                const enums = new Array<number>();
                const lowerInput = input.toLocaleLowerCase();

                for (const item of data.descriptions) {
                    const lowercaseDescription = item.Description.toLocaleLowerCase();
                    if (this._defaultWhisperer === StringComparisonTypes.startsWith) {
                        if (lowercaseDescription.startsWith(lowerInput)) {
                            enums.push(item.IntValue);
                        }
                    } else {
                        if (lowercaseDescription.indexOf(lowerInput) !== -1) {
                            enums.push(item.IntValue);
                        }
                    }
                }

                if (enums.length > 0) {
                    condition = `${attribute} IN [${enums.join(",")}]`; //`
                }
            }
        }

        return condition;
    }

    // Poskládá podmínku podle vstupu z inputu
    private async _buildConditionPlain(param: string): Promise<string> {
        let result: string = "";

        if (param != null || param !== "") {
            await Promise.all(this._showAttrs.map(async (attribute) => {
                if (attribute != null) {
                    const value = await this._buildConditionPart(attribute, param);

                    if (result && value) {
                        result += " OR ";
                    }

                    result += value;
                }
            }));
        }

        return result;
    }

    private async _buildCondition(param: string): Promise<string> {
        let result: string = "";

        if (param) {
            param = this._handleSpecialCharacters(param);
            const params = param.split(` ${this._separator} `); // Je potřeba to rozdělit, protože při automatickém doplnění hodnoty a novém hledaní už by tuto hodnotu nenašel

            if (params.length === 1) {
                return await this._buildConditionPlain(param);
            }

            let i = 0;

            await Promise.all(params.map(async (input) => {
                const value = await this._buildConditionPart(this._showAttrs[i++], input);

                if (result && value) {
                    result += " AND ";
                }

                result += value;
            }));
        }

        return result;
    }

    /**
     * Speciálním charakterům, které uživatel zadá (tj. \, ? a *) musí předcházet zpětné lomítko
     * @param param Původní podmínka
     * @returns Podmínka doplněna o zpětná lomítka
     */
    private _handleSpecialCharacters(param: string): string {
        param = param.replace(/\\/g, "\\\\");
        param = param.replace("*", "\*"); // eslint-disable-line no-useless-escape
        param = param.replace("?", "\?"); // eslint-disable-line no-useless-escape
        return param;
    }

    /** Poskládá hlavní podmínku pro daný objekt, podle condition a dateRelationAttr z definice LOV */
    private async _buildMainConditionAsync(): Promise<string> {
        this._dateRelAttr = this.getDataAttr("DateRelationAttr");
        let tempCond: string;

        const fncDate = (date: string) => {
            if (date != null && date !== "") {
                const dateCond: string = `(((Plati_OD <= ${date}) or (Plati_OD = null)) and ((Plati_DO >= ${date}) or (Plati_DO = null)))`;

                if (tempCond != null)
                    tempCond = tempCond + " and " + dateCond;
                else
                    tempCond = dateCond;
            }
        };

        if (this._condition != null)
            tempCond = "(" + this._condition + ")";

        if (this._dateRelAttr != null && this.actualObject != null) {
            if (this._dateRelAttr === "SYSDATE") {
                const val = SerializeFuncs.serializeJSON(new Date());
                fncDate(`'${val}'`);
            } else {
                try {
                    const val = await this.actualObject.getValue(this._dateRelAttr);
                    fncDate(val ? `'${SerializeFuncs.serializeJSON(val)}'` : `${null}`);
                } catch (x) {
                    // Prázdný catch
                }
            }
        }

        return tempCond;
    }

    /**
     * Metoda volaná při změně objektu (výběrem z našeptávače nebo z modálního okna)
     * @param value
     * @param callingEvent
     * @param btnClick - pokud je vyvoláno klikem na tlačítko LOV tak je true
     * @returns boolean promise - status průběhu, true v pořádku
     */
    protected override async _changeObject(value: string, callingEvent: Event, btnClick: boolean = false): Promise<boolean> {
        if (!btnClick && (value == null || value === "")) { // Při kliku na LOV button nechceme ztratit předchozí hodnotu
            this._recordId = undefined;
            this._lastObject = null;
            this._whispererDataLoading = false;
            await this._setSelectedRecordId(null);
            this._waitOnDataLoadedDeffer.resolve();
            return false;
        }

        if (!await super._changeObject(null, callingEvent, btnClick)) {
            return false;
        }

        let cond: string;
        if (value)
            cond = await this._buildCondition(value);
        else
            cond = "ID <> 0";

        //Podmínka pro případ, kdy je showAttrs typu ENUM a neodpovídá mu žádná hodnota. Je proto potřeba jen vymazat hodnoty z našeptávače.
        if (value && cond === "") {
            this._dataSource.clearData();
            return false;
        }

        if (this._mainCondition != null)
            cond = StringUtils.combineConditions(ConditionOperator.and, this._mainCondition, cond);

        cond = await this._getCompetenceCondition(cond);

        this._dataSource.setAttrs(this._showAttrs);
        this._dataSource.condition = cond;
        this._dataSource.conditionParams = this._currentConditionParams;
        await this._dataSource.loadNewData(false);
        return true;
    }

    protected override _setEnabledInput(value: boolean): void {
        super._setEnabledInput(value);
        this.wasClicked = false;

        if (this._lovButton && ("readOnly" in this._lovButton))
            (this._lovButton as any)["readOnly"] = !value;
        else if (this._lovButton && ("disabled" in this._lovButton))
            (this._lovButton as any)["disabled"] = false; // !value; - Zůstává povoleno, aby se na něj dalo kliknout
    }

    /**
     * Inicializace našeptávače (datagridu)
     */
    protected override async _initializeWhisperer(): Promise<boolean> {
        if (!await super._initializeWhisperer()) {
            return false;
        }

        const objectClass = (await ModelDescriptionsManager.instance.getClassInfo(this.className));

        this._orderBy = new Array<IOrderBy>();
        const orderBy = this.getDataAttr("OrderBy");

        if (orderBy) {
            this._orderBy = FrameSharedMethods.parseOrderBy(orderBy);
            if (this._orderBy)
                for (const order of this._orderBy) {
                    const attrType = (await objectClass.getAttributeInfo(order.attributeName)).basicType;
                    if (attrType == BasicDataType.String || attrType == BasicDataType.StrNum)
                        order.additionalOrdering = AdditionalOrdering.linguistic;
                }
        } else {
            if (this._showAttrs) {
                for (const attr of this._showAttrs) {
                    const attrType = (await objectClass.getAttributeInfo(attr)).basicType;
                    const additionalOrdering = attrType == BasicDataType.String || attrType == BasicDataType.StrNum ? AdditionalOrdering.linguistic : null;

                    this._orderBy.push({
                        attributeName: attr,
                        order: OrderType.ascending,
                        additionalOrdering: additionalOrdering,
                    });
                }
            } else {
                this._orderBy.push({
                    attributeName: "ID",
                    order: OrderType.ascending,
                });
            }
        }

        this._whisperer.orderBy = this._orderBy;
        this._whisperer.dataSource.orderBy = this._orderBy;

        this._events.addListener(this._whisperer, "SelectionChanged", async (e: IDataGridSelectionChangedEvent) => {
            if (this._whisperer.dataRows.length > 0 && e.detail.rowID !== "-1" && !e.detail.data.isRefreshSelection) {
                this._setSelectedRecordId(e.detail.rowID);
                this._whisperer.clearSelectedRows();
            }
        });

        this._events.addListener(this._whisperer, "DataShown", async () => { // DataShown se volá jen jednou po zobrazení všech dávek (viz. implementace DataGrid)
            if (this._hasFocus && ((this._whisperer.dataRows.length > 0 && this._lovButtonClicked) || (this._whisperer.dataRows.length > 1) && ((this.innerElement.value !== "") || ((this.innerElement.value === "") && (this._lovButtonClicked || this._showByKey))))) {
                await this.showDropDown();
            } else {
                if (this._whisperer && this._whisperer.dataRows.length === 1) {
                    const objID = this._whisperer.dataRows[0].dataKey;
                    await this._setSelectedRecordId(objID);
                    this.hideDropDown();
                } else {
                    if (this._lastObject != null) {
                        if (this.innerElement.value === "" && this._recordId == null)
                            await this._setSelectedRecordId(null);
                    }
                }
            }

            this._whispererDataLoading = false;
            this._lovButtonClicked = false;

            this._waitOnDataLoadedDeffer.resolve();
        });

        // Při nahrávání dat skrze DBDataSource služba vrátila chybu a proto je potřeba LOVEdit vyčistit a připravit na další akce.
        this._events.add(this._whisperer.dataSource.error, (_datasource, error) => {
            // Zruší flag týkající se toho, zda je napsaná hodnota právě zadávána uživatelem.
            this._whispererDataLoading = false;
            // výchozí hodnota kliknutí na LOVEdit
            this._lovButtonClicked = false;

            // Zruší běžící procesy na komponentě.
            this.cancelPendingActions();
            // V průběhu nahrávání dat skrze dataSource služba vrátila chybu a proto je nutné manuálně rejectnout promise.
            // Kdyby se tak nestalo, formulář by při zahození nebo uložení uvázl v nekonečném čekání na dokončení promise.
            this._waitOnDataLoadedDeffer.reject(error);
            logger.error(error);
        });

        return true;
    }

    /**
     * Nastavení hodnot LOV (používá se, když nastavuji víc atributů)
     * @param value nastavovaná hodnota
     */
    private async _updateValue(showAttrs: string[], obj: OWObject, resultString: string): Promise<void> {
        const attrPromises: Promise<{ attrName: string; value: IAttrValue; type: OWDataType }>[] = [];

        const getAttrValue = async function(obj: OWObject, attrName: string, types: { [key: string]: ColumnType }): Promise<{ attrName: string; value: IAttrValue; type: OWDataType }> {
            return {
                attrName: attrName,
                value: await obj.getValue(attrName).catch(() => null),
                type: types[attrName]?.type,
            };
        };
        showAttrs.forEach((attr) => attrPromises.push(getAttrValue(obj, attr, this._columnTypes)));

        this._pendingTask = Promise.all(attrPromises).then((values) => {
            if (this._pendingTaskCTS?.token.cancellationRequested) {
                return;
            }
            values.forEach((attrValue) => {
                let formattedValue = Globalization.formatValue(attrValue.value);
                // Nahradime v textu oddelovac za alternativni oddelovac, aby to nedelalo problem pri pripadnem oddelovani
                formattedValue = formattedValue.split(this._separator).join(this._separatorAlternative);

                if (attrValue.type === OWDataType.htmlText)
                    formattedValue = StringUtils.stripHTMLFromText(formattedValue);

                if (resultString)
                    resultString = `${resultString} ${this._separator} ${formattedValue}`; // `
                else
                    resultString = formattedValue;
            });

            if (this.innerElement) {
                this.innerElement.value = resultString;
                this._updateFilledClass(true);
            }

            this.setValid();
            this._waitOnDataLoadedDeffer.resolve();
        }, () => this._waitOnDataLoadedDeffer.reject()).finally(() => this.hideLoader());
    }

    private _updateFilledClass(isFilled: boolean): void {
        if (isFilled && this._lovStyle === LOVType.modal) {
            this.element.classList.add("LOVEdit--filled");
        } else {
            this.element.classList.remove("LOVEdit--filled");
        }
    }

    /** Nastaví validní objekt
     * Změna hodnoty na LOV z GUI->
     * 1. Volá se metoda setValidObject, kde se nastaví proměnná lovObjectChangeFromCombobox na nové ID a detail se dozví že došlo ke změně hodnoty na LOV
     * 2. Detail se ptá LOVeditu na novou hodnotu a ten mu vrací proměnou lovObjectChangeFromCombobox, kterou současně nulluje
     * 3. Detail aktualizuje hodnotu atributu (např. Druh_MJ)
     * 4. Detail volá na LOVEditu metodu set controlValue s novým objektem v parametru
     * 5. LOV prostřednictvím metody changeValueOnMaster aktualizuje zobrazovanou hodnotu
     *
     * Pokud se mění actualObject výběrem na listFramu tak se začíná od kroku 3.
     */
    private async _setSelectedRecordId(recordId: string | number | null): Promise<void> {
        this.setValid();

        if (this._dropDown && this._dropDown.visible) {
            this.hideDropDown();
        }

        const idNumValue = typeof recordId === "string" ? parseInt(recordId) : recordId;

        if (idNumValue != null && idNumValue > 0) {
            if (this._recordId != null && recordId === this._recordId)
                return;

            this._recordId = recordId.toString();

            // v případě, že je naplněno validationClassName, tak musím dočíst objekt, aby se na změnu poslala instance a ne jenom ID, protože server nezná správnou třídu
            this._lastObject = this._validationClassName ? await OWManager.getObject(this._recordId, this._validationClassName, undefined, undefined, undefined, this._getRequestContext()) : null;
        } else {
            this.innerElement.value = "";
            this._recordId = null;
            this._lastObject = null;
        }

        this.valueChanged.trigger(this, this._recordId);
    }

    protected override _loadDataToWhisperer(event: Event): Promise<boolean> {
        this._whispererDataLoading = true;
        this._recordId = undefined;
        this._lastObject = null;

        return super._loadDataToWhisperer(event);
    }

    private _clickListenerDebounced = FunctionUtils.debouncedCancelableFunctionAsync(async () => {
        try {
            this.focus(); // V některých prohlížečích mohlo dojít ke ztrátě focusu při kliku na lovbutton

            /* TODO: neumime pracovat s conditionParams
                var params: string[] = new Array<string>();
                if (this.conditionParams)
                    this.conditionParams.split(';').forEach((akt) => { params.push(<string>this.ActualObject.GetValue(akt).result) });
                */
            if (!this.isEditing || !this.enabled) {
                if (this._lovStyle === LOVType.dropdown)
                    return;

                if (!this.interactive) {
                    return; // Přes form je vykresleno jiné modální okno
                } else {
                    if (this.actualObject != null) {
                        const val = await this.actualObject.getValue(this.attrName);
                        if (val == null || val instanceof NullOWObject) return;

                        let condition: string = "(ID = 0)";

                        if (val instanceof IOWObject && !val.isNew) {
                            condition = "(ID = " + val.id + ")";
                        }

                        if (this._whisperer)
                            this._whisperer.clearDataRows();

                        let type: ModalWindowType;
                        let params: IFRMParams = {
                            condition: condition,
                            conditionParamsAttrs: this._conditionParamsAttrs,
                            conditionParamsObject: this._conditionParamsAttrs != null ? this.actualObject : null,
                            ownerForm: this._ownerForm,
                            readVersioned: this._readVersioned,
                            formContext: this.frameContext,
                            transactionContextGetter: this.frameContext?.getTransactionContextId,
                            visibleOnlyDetail: this._showOnlyDetail,
                        };

                        if (this._showAsLink) {
                            type = ModalWindowType.parametricForm;
                            params = {
                                ...params,
                                inLOVMode: false,
                                applyLOVCondition: false,
                                hideWindowActions: true,
                            };
                        } else {
                            type = ModalWindowType.lov;
                            params = {
                                ...params,
                                inLOVMode: true,
                                applyLOVCondition: true,
                                enableLOVSelect: false,
                                showDetail: this._showDetail,
                            };
                        }

                        try {
                            await LOVEditMaster.openDetailForm({
                                object: this.actualObject,
                                attrName: this.attrName,
                                formName: this._editWindow,
                                onClose: () => self.setTimeout(() => {
                                    if (this.innerElement) {
                                        this.innerElement.focus();
                                    }
                                }, 500),
                                parametricData: params,
                            }, type);

                            if (this._dropDown && this._dropDown.visible)
                                this.hideDropDown();
                            if (this._errorPopup && this._errorPopup.visible)
                                this._errorPopup.setVisibility(false);
                        } catch (e) {
                            ErrorHandler.instance.handleError(e);
                            return;
                        }
                        return;
                    } else {
                        return;
                    }
                }
            }

            if (this._lovStyle == LOVType.dropdown) this._lovButtonClicked = true;
            if (this._lovStyle === LOVType.modal) {
                const condition = await this._getCompetenceCondition(this._mainCondition);
                if (this._whisperer)
                    this._whisperer.clearDataRows();

                try {
                    await LOVEditMaster.openDetailForm({
                        object: this.actualObject,
                        attrName: this.attrName,
                        formName: this._editWindow,
                        onClose: (_sender, result) => {
                            if (result && result.resultType === FormResultType.ok && result.resultValue instanceof IRecordIdentification) {
                                this._setSelectedRecordId(result.resultValue.id);
                            }

                            self.setTimeout(() => {
                                this.innerElement.focus();
                            }, 0);
                        },
                        parametricData: {
                            condition: condition,
                            conditionParamsAttrs: this._conditionParamsAttrs,
                            conditionParamsObject: this._conditionParamsAttrs != null ? this.actualObject : null,
                            inLOVMode: true,
                            applyLOVCondition: true,
                            enableLOVSelect: true,
                            showDetail: this._showDetail,
                            ownerForm: this._ownerForm, // Jinak na něm nepůjde zakázat/skrýt backButton formuláře
                            readVersioned: this._readVersioned,
                            formContext: { ...this.frameContext, roles: this._actualObject.editRoleIds },
                            transactionContextGetter: this.frameContext?.getTransactionContextId,
                            isImplicit: FrameSharedMethods.isFormNameImplicit(this._editWindow),
                        },
                    }, ModalWindowType.lov);

                    if (this._dropDown && this._dropDown.visible)
                        this.hideDropDown();
                    if (this._errorPopup && this._errorPopup.visible)
                        this._errorPopup.setVisibility(false);
                } catch (e) {
                    ErrorHandler.instance.handleError(e);
                    return;
                }
            } else {
                if (this._dropDown && this._dropDown.visible)
                    this.hideDropDown();
                else
                    await this._changeObject("", event, true);
            }
        } finally {
            this._isModalLoading = false;
        }
    }, 300);

    /**
     * Nastavení listenerů pro LOV
     */
    private _setListeners(): void {
        this._setDefaultListeners();
        this._events.addListener(this._showAsLink ? this.innerElement : this._lovButton, "click", async (event: MouseEvent) => {
            if (this.wasClicked && !this.isEditing) { // Pokud nejsme v editaci a již dříve bylo kliknuto (tudíž došlo k doubleclicku) => chceme zrušit otevírání modalu (a místo toho přepnout DF do editace)
                this._clickListenerDebounced.cancel();
                this._isModalLoading = false;
                return;
            }
            this.wasClicked = true;
            event.stopPropagation();
            if (!this._checkClickEvent(event))
                return;
            await this._clickListenerDebounced.callback();
        });

        this._events.addListener(this.innerElement, "keydown", async (e: KeyboardEvent) => {
            this._showByKey = false;
            this._keyboardEventHandler(e);

            // key enter
            if (this._dropDown && (e as KeyboardEvent).key === Code.Enter) {
                if (this._whisperer.dataRows.length === 1)
                    this._lovOnBlur(false);
                return;
            }
        });

        this._events.addListener(this.innerElement, "paste", (e) => {
            if (this._waitOnDataLoadedDeffer.isSettled) {
                this._waitOnDataLoadedDeffer = PromiseUtils.deferStateful<boolean>();
            }
            e.stopPropagation();
            this._loadDataToWhisperer(e);
            this.valueChanged.trigger(this, { Sender: this });
        });

        this._events.addListener(this.element, "focusout", (event: FocusEvent) => {
            /*Když uživatel klikne na prvek, který nemůže získat focus je event.relatedTarget naplněn hodnotou null.
            V takovém případě dojde k obsluze události focusout/blur na třídě LOVEditMaster v handleru události click na dokumentu.
            Pokud je event.relatedTarget nastaven a je vyhodnoceno, že focus získá prvek mimo komponentu LOVEdit
            dojde k obsluze události focusout/blur ihned.*/
            this.wasClicked = false;
            if (!event.relatedTarget) {
                this._lostFocusEvent = event;
            } else if (event.relatedTarget !== this.innerElement && event.relatedTarget !== this._lovButton && !(event.relatedTarget as HTMLElement).closest(".LOVWhisperer")) {
                this._canSelectOnBlur = true;
                this._lovOnBlur(true);
                this.lostFocus.trigger(this, event);
            }
        });

        this._events.addListener(this.innerElement, "blur", (event: FocusEvent) => {
            /*Když uživatel klikne na prvek, který nemůže získat focus je event.relatedTarget naplněn hodnotou null.
            V takovém případě dojde k obsluze události focusout/blur na třídě LOVEditMaster v handleru události click na dokumentu.
            Pokud je event.relatedTarget nastaven a je vyhodnoceno, že focus získá prvek mimo komponentu LOVEdit
            dojde k obsluze události focusout/blur ihned.*/
            if (!event.relatedTarget) {
                this._lostFocusEvent = event;
            } else if (event.relatedTarget !== this.element && !(event.relatedTarget as HTMLElement).closest(".LOVWhisperer")) {
                if (this._dropDown) {
                    this._canSelectOnBlur = true;
                    this._lovOnBlur(true);
                } else {
                    //pokud není inicializován dropDown je třeba spustit validaci
                    this.validate();
                }
                //Focus získal jiný element mimo LOV spouštíme lostFocus event
                if (event.relatedTarget !== this._lovButton) {
                    this.lostFocus.trigger(this, event);
                }
            }
        });

        this._events.addListener(this.element, "focus", (event: FocusEvent) => {
            this.getFocus.trigger(this, event);
            this._lostFocusEvent = undefined;
        }, { options: { capture: true } });
    }

    protected override _changeEventHandler(): void {
        // Prázdná metoda
    }

    protected override _editableControlClicked(sender: EditableControl, event: MouseEvent): void {
        super._editableControlClicked(sender, event);

        if (this._lovButton === document.activeElement && this._lovStyle !== LOVType.dropdown && this.isEditing) { // Nechceme, aby při kliku na LovButton pro modální okno přešel detail do editace
            EventHelper.stopPropagation(event);
        }
    }

    /**
     * Předá focus na input
     */
    public override focus(): void {
        this.innerElement.focus();
    }
    //#endregion Methods

    //#region Dispose
    public override dispose(): void {
        this._actualObject = null;
        if (this._dropDown) {
            this._dropDown.element.remove();
        }

        this._whisperer?.dispose();
        this.cancelPendingActions();
        super.dispose();
    }
    //#endregion Dispose
}

enum LOVType {
    modal = "lsmodal",
    dropdown = "lsdropdown",
}
